import {useState} from 'react';
import {
  findBestPrice,
  UpsellBillingCycle,
} from '@common/billing/pricing-table/find-best-price';
import {useProducts} from '@common/billing/pricing-table/use-products';
import {Product} from '@common/billing/product';
import {Price} from '@common/billing/price';
import {useAuth} from '@common/auth/use-auth';
import {useBillingUser} from '@common/billing/billing-page/use-billing-user';
import {apiClient} from '@common/http/query-client';
import {Link} from 'react-router-dom';
import {toast} from '@common/ui/toast/toast';
import {useTrans} from '@common/i18n/use-trans';
import {message} from '@common/i18n/message';

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{width: 16, height: 16}}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

export function PricingSection() {
  const query = useProducts();
  const {isLoading: subscriptionLoading} = useBillingUser();
  const [selectedCycle, setSelectedCycle] =
    useState<UpsellBillingCycle>('monthly');

  const isLoading = query.isLoading || subscriptionLoading;

  return (
    <section id="pricing" className="section pricing-section">
      <div className="fade-up">
        <div className="section-label">Pricing</div>
        <h2 className="section-title">
          Start free. Launch when you&apos;re ready.
        </h2>
        <p className="section-sub">
          No hidden fees. 30-day money-back guarantee on all paid plans.
        </p>
      </div>
      <div className="pricing-cycle-toggle">
        <button
          className={selectedCycle === 'monthly' ? 'active' : ''}
          onClick={() => setSelectedCycle('monthly')}
        >
          Monthly
        </button>
        <button
          className={selectedCycle === 'yearly' ? 'active' : ''}
          onClick={() => setSelectedCycle('yearly')}
        >
          Yearly
        </button>
        <span className="save-badge">Save up to 30%</span>
      </div>
      {!isLoading && query.data ? (
        <PricingLayout
          plans={query.data.products}
          selectedCycle={selectedCycle}
        />
      ) : (
        <PricingCardsSkeleton />
      )}
      <p className="pricing-payment-label">Secured payment with PayPal</p>
      <div className="pricing-payment-logos">
        <img src="/assets/images/frontend-pages/visa.png" alt="Visa" />
        <img src="/assets/images/frontend-pages/mastercard.png" alt="Mastercard" />
        <img src="/assets/images/frontend-pages/americanexpress.png" alt="American Express" />
        <img src="/assets/images/frontend-pages/paypal.png" alt="PayPal" />
        <img src="/assets/images/frontend-pages/maestro.png" alt="Maestro" />
        <img src="/assets/images/frontend-pages/jcb.png" alt="JCB" />
        <img src="/assets/images/frontend-pages/dinersclub.png" alt="Diners Club" />
        <img src="/assets/images/frontend-pages/discover.png" alt="Discover" />
      </div>
    </section>
  );
}

interface PricingLayoutProps {
  plans: Product[];
  selectedCycle: UpsellBillingCycle;
}

function PricingLayout({plans, selectedCycle}: PricingLayoutProps) {
  const {isLoggedIn} = useAuth();
  const filteredPlans = plans.filter(plan => !plan.hidden);

  const freePlan = filteredPlans.find(plan => plan.free);
  const paidPlans = filteredPlans.filter(plan => !plan.free);

  return (
    <div className="pricing-layout">
      {freePlan && (
        <FreeTierBanner plan={freePlan} selectedCycle={selectedCycle} isLoggedIn={isLoggedIn} />
      )}
      <PaidPlanCards plans={paidPlans} selectedCycle={selectedCycle} />
      <CustomPlanCta />
    </div>
  );
}

interface FreeTierBannerProps {
  plan: Product;
  selectedCycle: UpsellBillingCycle;
  isLoggedIn: boolean;
}

function FreeTierBanner({plan, selectedCycle, isLoggedIn}: FreeTierBannerProps) {
  const {user} = useAuth();
  const {subscription} = useBillingUser();
  const {trans} = useTrans();
  const [loading, setLoading] = useState(false);

  // Check if user already has the free plan (or any plan — free is the base)
  const isCurrentPlan = Boolean(
    subscription?.product?.name &&
    subscription.product.name.toLowerCase().includes('free')
  );

  const handleActivate = async () => {
    if (!user) return;
    setLoading(true);

    const host = window.location.origin;
    const successUrl = `${host}/billing/success?product=free&interval=month&count=1`;

    try {
      const res = await apiClient.post('billing/checkout', {
        customer_id: String(user.id),
        email: user.email,
        name: user.display_name || user.email,
        product_id: plan.id,
        success_url: successUrl,
        force_checkout: true,
        interval: 'month',
        interval_count: 1,
      });

      const data = res.data as any;
      if (data?.redirect_url) {
        window.location.href = data.redirect_url;
      } else if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data?.url) {
        window.location.href = data.url;
      } else {
        window.location.href = `${host}/billing/success?product=free&interval=month&count=1&immediate=1`;
      }
    } catch (e) {
      console.error('Free plan activation error:', e);
      toast(trans(message('Failed to activate free plan. Please try again.')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="free-tier-banner">
      <div className="free-tier-content">
        <div className="free-tier-left">
          <span className="free-tier-badge">Free</span>
          <span className="free-tier-text">
            Get started with 1 site, 100 AI generations, and 10 GB storage — no credit card required.
          </span>
        </div>
        {!isLoggedIn ? (
          <Link to="/register?redirectFrom=pricing" className="free-tier-btn">
            Start Free <ArrowIcon />
          </Link>
        ) : isCurrentPlan ? (
          <span className="free-tier-active">Active Plan</span>
        ) : (
          <button
            className="free-tier-btn"
            onClick={handleActivate}
            disabled={loading}
          >
            {loading ? 'Activating...' : 'Activate Free Plan'} {!loading && <ArrowIcon />}
          </button>
        )}
      </div>
      <div className="free-tier-features">
        {plan.feature_list.slice(0, 6).map((feature, i) => (
          <span key={i} className="free-tier-feature">
            <CheckIcon /> {feature}
          </span>
        ))}
      </div>
    </div>
  );
}

interface PaidPlanCardsProps {
  plans: Product[];
  selectedCycle: UpsellBillingCycle;
}

function PaidPlanCards({plans, selectedCycle}: PaidPlanCardsProps) {
  const {isLoggedIn, user} = useAuth();
  const {subscription} = useBillingUser();
  const {trans} = useTrans();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);

  const currentPlanId = subscription?.product?.name
    ? plans.find(
        plan =>
          plan.name === subscription.product.name &&
          findBestPrice(selectedCycle, plan.prices)?.interval ===
            subscription.price?.interval &&
          Number(
            findBestPrice(selectedCycle, plan.prices)?.interval_count ?? 1
          ) === Number(subscription.price?.interval_count ?? 1)
      )?.id
    : null;

  const handleCheckout = async (plan: Product, price: Price) => {
    if (!user || !price) return;
    if (currentPlanId === plan.id) return;

    const isScheduled = subscription?.product?.scheduled_changes?.some(
      (change: {product_id: string}) => change.product_id === plan.id
    );
    if (isScheduled) {
      toast(trans(message('You already have a scheduled change to this plan')));
      return;
    }

    setLoadingPlanId(plan.id);

    const hasExistingSubscription = subscription?.product?.name;
    const host = window.location.origin;
    const query = new URLSearchParams({
      product: plan.name.toLowerCase(),
      interval: String(price.interval),
      count: String(price.interval_count ?? 1),
    }).toString();
    const successUrl = `${host}/billing/success?${query}`;

    let payload;
    let endpoint;

    if (hasExistingSubscription) {
      payload = {
        customer_id: String(user.id),
        email: user.email,
        name: user.display_name || user.email,
        product: plan.id,
        interval: String(price.interval),
        interval_count: Number(price.interval_count ?? 1),
      };
      endpoint = 'billing/change-plan';
    } else {
      payload = {
        customer_id: String(user.id),
        email: user.email,
        name: user.display_name || user.email,
        product_id: price.product_id || plan.id,
        success_url: successUrl,
        force_checkout: true,
        interval: String(price.interval),
        interval_count: Number(price.interval_count ?? 1),
      };
      endpoint = 'billing/checkout';
    }

    try {
      const res = await apiClient.post(endpoint, payload);
      const redirect = (res.data as any)?.redirect_url;
      const checkoutUrl = (res.data as any)?.checkout_url;
      const autumnUrl = (res.data as any)?.url;

      if (redirect) {
        window.location.href = redirect;
      } else if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else if (autumnUrl) {
        window.location.href = autumnUrl;
      } else {
        const immediateSuccessUrl = `${window.location.origin}/billing/success?product=${plan.name.toLowerCase()}&interval=${price.interval}&count=${price.interval_count ?? 1}&immediate=1`;
        window.location.href = immediateSuccessUrl;
      }
    } catch (e) {
      console.error('Billing checkout error:', e);
      toast(trans(message('Failed to process billing request. Please try again.')));
    } finally {
      setLoadingPlanId(null);
    }
  };

  // Canonical features that all plans should display.
  // Plan-specific variants (storage, sites, AI gens) are matched by pattern
  // and shown with the plan's own value. Boolean features use the exact name.
  // Features a plan doesn't have are shown with an X at the bottom.
  const canonicalFeatures = [
    // These vary per plan — matched by pattern
    { pattern: /sites$/i, label: 'Sites' },
    { pattern: /AI Generations/i, label: 'AI Generations' },
    { pattern: /storage$/i, label: 'Storage' },
    // Boolean features — matched exactly
    { pattern: /Drag & Drop/i, label: 'Drag & Drop Editor' },
    { pattern: /AI Site Generation/i, label: 'AI Site Generation' },
    { pattern: /AI Image Generation/i, label: 'AI Image Generation' },
    { pattern: /BYOK/i, label: 'BYOK (Bring Your Own Key)' },
    { pattern: /Templates/i, label: 'Templates' },
    { pattern: /Add Ons/i, label: 'Add Ons: Rhumba AI' },
    { pattern: /History/i, label: 'History + Snapshots' },
    { pattern: /Free.*Subdomain/i, label: 'Free Landed Subdomain' },
    { pattern: /Custom Domain/i, label: 'Custom Domain' },
    { pattern: /Publish/i, label: 'Publish your site' },
    { pattern: /No Landed badge/i, label: 'No Landed badge on site' },
    { pattern: /Download Sites/i, label: 'Download Sites + Export via FTP' },
    { pattern: /Standard Support/i, label: 'Standard Support' },
    { pattern: /Commercial Rights/i, label: 'Commercial Rights' },
    { pattern: /Basic Analytics/i, label: 'Basic Analytics' },
    { pattern: /Webhook/i, label: 'Webhook Integrations (Advanced Lead Capture)' },
    { pattern: /Priority Support/i, label: 'Priority Support' },
    { pattern: /Custom Code|CSS\/JS\/HTML/i, label: 'Custom Code: CSS/JS/HTML Injection' },
    { pattern: /Model Context Protocol/i, label: 'Model Context Protocol' },
    { pattern: /White-labeling/i, label: 'White-labeling on Dashboard & Website' },
    { pattern: /Collaboration/i, label: 'Collaboration (Teams/Workspaces)' },
    { pattern: /VIP Support/i, label: 'VIP Support' },
  ];

  function buildFeatureRows(planFeatures: string[]) {
    const included: {label: string}[] = [];
    const excluded: {label: string}[] = [];

    for (const canon of canonicalFeatures) {
      const match = planFeatures.find(f => canon.pattern.test(f));
      if (match) {
        included.push({ label: match });
      } else {
        excluded.push({ label: canon.label });
      }
    }

    return { included, excluded };
  }

  return (
    <div className="pricing-grid three-col">
      {plans.map((plan, index) => {
        const price = findBestPrice(selectedCycle, plan.prices);
        const isFeatured = plan.recommended || plan.name.toLowerCase().includes('pro');
        const isCurrent = currentPlanId === plan.id;
        const isLoading = loadingPlanId === plan.id;
        const { included, excluded } = buildFeatureRows(plan.feature_list);

        const cardClass = [
          'pricing-card',
          isFeatured ? 'featured' : '',
          isCurrent ? 'current-plan' : '',
        ]
          .filter(Boolean)
          .join(' ');

        const amount = price
          ? price.interval === 'year'
            ? Math.round(price.amount / 12)
            : Math.round(price.amount / (price.interval_count ?? 1))
          : 0;

        const otherCyclePrice =
          selectedCycle === 'monthly'
            ? findBestPrice('yearly', plan.prices)
            : findBestPrice('monthly', plan.prices);
        const yearlyEquivalentMonthly = otherCyclePrice?.interval === 'year'
          ? Math.round(otherCyclePrice.amount / 12)
          : otherCyclePrice?.amount ?? 0;
        const descText =
          selectedCycle === 'monthly' && otherCyclePrice
            ? `$${yearlyEquivalentMonthly}/mo billed annually`
            : selectedCycle === 'yearly'
              ? `$${amount}/mo billed annually`
              : plan.description;

        return (
          <div
            key={plan.id}
            className={cardClass}
            style={index > 0 ? {transitionDelay: `${index * 0.08}s`} : undefined}
          >
            {isFeatured && (
              <div className="pricing-popular">Most Popular</div>
            )}
            <div className="pricing-label">{plan.name}</div>
            <div className="pricing-price">
              <sup>$</sup>
              {amount}
              <span>/mo</span>
            </div>
            <p className="pricing-desc">{descText}</p>
            <ul className="pricing-features">
              {included.map((feat, i) => (
                <li key={`inc-${i}`}>
                  <CheckIcon />
                  {feat.label}
                </li>
              ))}
              {excluded.map((feat, i) => (
                <li key={`exc-${i}`} className="excluded">
                  <XIcon />
                  {feat.label}
                </li>
              ))}
            </ul>
            {isCurrent ? (
              <button className="pricing-btn outline" disabled>
                Current Plan
              </button>
            ) : isLoading ? (
              <button
                className={`pricing-btn ${isFeatured ? 'filled' : 'outline'}`}
                disabled
              >
                Redirecting...
              </button>
            ) : !isLoggedIn ? (
              <Link
                to="/register?redirectFrom=pricing"
                className={`pricing-btn ${isFeatured ? 'filled' : 'outline'}`}
              >
                Get Started
              </Link>
            ) : (
              <button
                className={`pricing-btn ${isFeatured ? 'filled' : 'outline'}`}
                onClick={() => price && handleCheckout(plan, price)}
              >
                Get Started
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CustomPlanCta() {
  return (
    <div className="custom-plan-cta">
      <div className="custom-plan-content">
        <div className="custom-plan-left">
          <h3 className="custom-plan-title">Need more?</h3>
          <p className="custom-plan-text">
            Get a custom plan with tailored site limits, AI generations, storage, white-labeling, and dedicated support.
          </p>
        </div>
        <a href="/contact" className="pricing-btn outline custom-plan-btn">
          Contact Us <ArrowIcon />
        </a>
      </div>
    </div>
  );
}

function PricingCardsSkeleton() {
  return (
    <div className="pricing-layout">
      <div className="free-tier-banner" style={{opacity: 0.5}}>
        <div style={{background: 'var(--border)', borderRadius: 8, height: 40, width: '100%'}} />
      </div>
      <div className="pricing-grid three-col">
        {[0, 1, 2].map(i => (
          <div key={i} className="pricing-card" style={{minHeight: 420}}>
            <div style={{background: 'var(--border)', borderRadius: 8, height: 14, width: 60, marginBottom: 12}} />
            <div style={{background: 'var(--border)', borderRadius: 8, height: 48, width: 120, marginBottom: 8}} />
            <div style={{background: 'var(--border)', borderRadius: 8, height: 14, width: 140, marginBottom: 28}} />
            {[0, 1, 2, 3].map(j => (
              <div key={j} style={{background: 'var(--border)', borderRadius: 8, height: 14, width: '80%', marginBottom: 16}} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
